import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * Animated walkthrough of posting a private comment: type → flip the
 * visibility toggle to Private → post → the comment lands with the private
 * treatment (amber left border + gradient), exactly as CaseDetail renders it.
 *
 * All markup borrows the real classes from Comments.jsx (.composer,
 * .vis-toggle, .vis-opt, .comment.private) so the demo can never drift from
 * the actual look. Demo data is placeholder-only.
 */

const DRAFT = 'Escalating to the medical team for review.';

const STEPS = [
  {
    caption: 'Write your comment in the composer as usual.',
    scene: 'typing', ms: 2400,
    cursor: 'input',
  },
  {
    caption: 'Comments post as Public unless you switch the toggle.',
    scene: 'typed', ms: 2000,
    cursor: 'toggle',
  },
  {
    caption: 'Click Private — only HoRO, Admin and medical roles will see it.',
    scene: 'private', ms: 2200,
    cursor: 'toggle', click: true,
  },
  {
    caption: 'Post the comment.',
    scene: 'posting', ms: 1600,
    cursor: 'post', click: true,
  },
  {
    caption: 'Private comments get an amber edge — Clinic Managers never see them in the thread.',
    scene: 'posted', ms: 3200,
  },
];

function VisToggle({ isPublic }) {
  return (
    <div className="vis-toggle" data-kb-target="toggle">
      <span className={`vis-opt public${isPublic ? ' active' : ''}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        Public
      </span>
      <span className={`vis-opt private${!isPublic ? ' active' : ''}`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        Private
      </span>
    </div>
  );
}

export default function CommentToggleDemo() {
  return (
    <DemoStage steps={STEPS} label="Posting a private comment" height={250}>
      {(step) => {
        const scene = step?.scene ?? 'typing';
        const isPublic = scene === 'typing' || scene === 'typed';
        const posted = scene === 'posted';
        const text = scene === 'typing' ? DRAFT.slice(0, 18) + '…' : DRAFT;
        return (
          <div className="kb-mock">
            {/* Composer */}
            <div className="composer" style={{ opacity: posted ? 0.45 : 1, marginBottom: 0 }}>
              <div className="kb-mock-input" data-kb-target="input">
                {posted ? <span style={{ color: 'var(--text-3)' }}>Add a comment…</span> : text}
              </div>
              <div className="composer-foot">
                <span className="kb-skel" style={{ width: 54 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <VisToggle isPublic={isPublic || posted} />
                  <button type="button" className="btn primary sm" tabIndex={-1} data-kb-target="post">Post</button>
                </div>
              </div>
            </div>

            {/* Thread — the new comment appears on post */}
            {posted && (
              <div className="comment private kb-pop" style={{ marginTop: 12 }}>
                <span className="kb-mock-avatar">AK</span>
                <div className="comment-body">
                  <div className="comment-head">
                    <span className="comment-name">A. Kelly</span>
                    <span className="comment-role">Admin</span>
                    <span className="comment-time">just now</span>
                  </div>
                  <div className="comment-text">{DRAFT}</div>
                </div>
              </div>
            )}
          </div>
        );
      }}
    </DemoStage>
  );
}
